import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { FileUpload, FileUploadModule } from "primeng/fileupload";

import { PaymentProvider, Repayment, Repayments } from "@/domain/repayments";
import {
  REPAYMENT_DATA_SOURCE_OPTIONS,
  REPAYMENT_DESCRIPTION_OPTIONS,
} from "@/domain/repayment-options";
import { isTerminalRequired } from "@/domain/repayment-validation";
import { QueryParamsService } from "@/service/query-params.service";
import { RepaymentsService } from "@/service/repayments.service";
import { UploadedFileService } from "@/service/uploaded-file.service";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Buffer } from "buffer";
import * as officeCrypto from "officecrypto-tool";
import { ConfirmationService, MessageService } from "primeng/api";
import { ConfirmPopupModule } from "primeng/confirmpopup";
import { ListboxModule } from "primeng/listbox";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ToastModule } from "primeng/toast";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as cptable from "xlsx/dist/cpexcel.js";
import * as XLSX from "xlsx/dist/xlsx.full.min.js";

if ("set_cptable" in XLSX && typeof XLSX.set_cptable === "function") {
  XLSX.set_cptable(cptable);
}

interface SpreadsheetRow {
  values: any[];
  rawValues: any[];
  rowNumber: number;
}

interface ExtractedRows {
  header: string[];
  rows: SpreadsheetRow[];
  headerRowIndex: number;
}

interface ParsedRepaymentRow extends Partial<Repayment> {
  description?: string;
  repayment_date?: string;
  payment_amount?: number | string;
  beneficiary_sort_code?: string | null;
  beneficiary_account_number?: string | null;
  terminal_name?: string;
  terminal_id?: string;
  cash_advance_name?: string;
  cash_advance_id?: string;
  customer_number?: string;
  row_number: number;
}

const CUSTOM_TEMPLATE_COLUMNS: Record<string, keyof ParsedRepaymentRow> = {
  "repayment date": "repayment_date",
  description: "description",
  amount: "payment_amount",
  "data source": "data_source",
  "365 split": "x365_split",
  comments: "comments",
  terminal: "terminal_name",
  "terminal id": "terminal_id",
  "cash advance": "cash_advance_name",
  "cash advance id": "cash_advance_id",
  "customer number": "customer_number",
};

const REQUIRED_CUSTOM_TEMPLATE_COLUMNS: Record<string, keyof ParsedRepaymentRow> =
  {
    "repayment date": "repayment_date",
    description: "description",
    amount: "payment_amount",
    "data source": "data_source",
    "365 split": "x365_split",
    comments: "comments",
  };

@Component({
  selector: "app-upload-file-dialog",
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    FileUploadModule,
    ConfirmPopupModule,
    ToastModule,
    ProgressSpinnerModule,
    ListboxModule,
    FormsModule,
  ],
  templateUrl: "./upload-file-dialog.component.html",
  styleUrl: "./upload-file-dialog.component.scss",
  providers: [ConfirmationService, MessageService],
})
export class UploadFileDialogComponent implements OnChanges, OnDestroy {
  @Input()
  visible: boolean = false;
  @Output()
  closeDialog: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild("fileUpload") fileUploadComp!: FileUpload;

  countries = [
    { name: "UK", code: "uk" },
    { name: "Ireland", code: "ie" },
  ];

  selectedCountry: any = this.countries[0];

  fileName: string = "";

  public isLoading: boolean = false;
  public fileExists: boolean = false;
  private readonly destroy$ = new Subject<void>();
  private readonly lifecycleAbort = new AbortController();
  private readonly timeouts = new Set<ReturnType<typeof setTimeout>>();
  private activeFileReader: FileReader | null = null;
  private destroyed = false;

  constructor(
    private readonly uploadedFileService: UploadedFileService,
    private readonly repaymentsService: RepaymentsService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly queryParamsService: QueryParamsService,
    private readonly messageService: MessageService,
  ) {
    this.queryParamsService.queryParams$
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (this.destroyed) {
          return;
        }
        this.selectedCountry =
          this.countries.find(
            (country) => country.code === params["country"],
          ) || this.countries[0];
      });
  }

  ngOnChanges(changes): void {
    if (this.destroyed) {
      return;
    }
    if (changes["visible"] && this.visible) {
      this.fileUploadComp?.clear();
      this.fileName = "";
    }
  }

  onFileSelect(ev) {
    if (this.destroyed) {
      return;
    }
    let workBook = null;
    const reader = new FileReader();
    const file = ev.files?.[0];
    if (!file) {
      return;
    }
    // Set this before FileReader begins so parsing/decryption never looks like an
    // unresponsive upload.
    this.isLoading = true;
    this.fileName = file.name;
    this.activeFileReader = reader;
    reader.onerror = () => {
      this.activeFileReader = null;
      this.isLoading = false;
      this.notifyError(
        "Unable to read file",
        "The selected file could not be read. Please try again.",
      );
    };
    reader.onload = async () => {
      if (this.destroyed) {
        this.activeFileReader = null;
        this.isLoading = false;
        return;
      }
      const data = reader.result;
      let binaryString = this.ensureBinaryString(data);
      let binaryData = this.ensureBinaryData(data);
      if (!binaryString && !binaryData) {
        this.notifyError(
          "Unable to read file",
          "The selected file could not be processed.",
        );
        this.activeFileReader = null;
        this.isLoading = false;
        return;
      }
      let provider: PaymentProvider;
      try {
        const detected = await this.detectProviderAndReadWorkbook({
          binaryString: binaryString || undefined,
          byteArray: binaryData || undefined,
        });
        provider = detected.provider;
        workBook = detected.workBook;
        this.fileName = `${file.name} (${provider})`;
      } catch (error) {
        console.error("Failed to identify spreadsheet", error);
        this.notifyError(
          "Unrecognized file",
          this.extractErrorMessage(
            error,
            "This file does not match an APS, CardOne, Cardnet, Clover, Gocardless, or Custom template.",
          ),
        );
        this.activeFileReader = null;
        this.isLoading = false;
        return;
      }

      let parsedRows = this.parseRepaymentsFromWorkbook(workBook, provider);

      if (this.destroyed) {
        this.activeFileReader = null;
        this.isLoading = false;
        return;
      }
      if (!parsedRows.length) {
        this.notifyError(
          "No transactions found",
          "The file does not contain any repayment rows.",
        );
        this.activeFileReader = null;
        this.isLoading = false;
        return;
      }

      try {
        if (provider === PaymentProvider.CUSTOM) {
          parsedRows = await this.validateAndPrepareCustomRows(parsedRows);
          if (!parsedRows.length || this.destroyed) {
            return;
          }
        }
        const existingFileID = await this.uploadedFileService.doesFileExist(
          file,
          { signal: this.lifecycleAbort.signal },
        );

        if (this.destroyed) {
          return;
        }

        if (!existingFileID) {
          const createdFileID = await this.uploadedFileService.addFile(
            file,
            this.selectedCountry,
            provider,
            { signal: this.lifecycleAbort.signal },
          );

          if (this.destroyed) {
            return;
          }
          if (!createdFileID) {
            return;
          }
          const newRepayments = this.prepareUploadedRepayments(
            parsedRows,
            createdFileID,
            provider,
          );

          await this.repaymentsService.addRepayments(newRepayments, {
            signal: this.lifecycleAbort.signal,
          });
          if (this.destroyed) {
            return;
          }
          // A new file is always unsaved. Validate its staged rows only when the
          // user commits it, so unresolved mappings remain actionable edits rather
          // than making a successful upload look like an error.
          // Make the count available immediately, then reconcile it with the
          // database view. The optimistic value avoids a stale zero/blank row.
          await this.uploadedFileService.updateFile(
            {
              ...this.uploadedFileService.files.find(
                (uploaded) => uploaded.id === createdFileID,
              )!,
              id: createdFileID,
              repayment_count: newRepayments.length,
              status: "UNSAVED",
            },
            { signal: this.lifecycleAbort.signal },
          );
          await this.uploadedFileService.loadFileByID(createdFileID, {
            signal: this.lifecycleAbort.signal,
          });
          this.uploadedFileService.setFileRepaymentCount(
            createdFileID,
            newRepayments.length,
          );
          if (this.destroyed) {
            return;
          }

          this.closeDialog.emit();
        } else {
          this.scheduleTask(() => {
            this.showExistsDialog(existingFileID);
          });
        }
      } catch (error) {
        if (this.isAbortError(error)) {
          return;
        }
        console.error("Upload failed", error);
        this.notifyError(
          "Upload failed",
          this.extractErrorMessage(
            error,
            "The file was not uploaded. Check the template values and try again.",
          ),
        );
      } finally {
        this.activeFileReader = null;
        if (!this.destroyed) {
          this.isLoading = false;
        }
      }
    };
    reader.readAsArrayBuffer(file);
  }

  showExistsDialog(existingFileID) {
    if (this.destroyed) {
      return;
    }
    this.fileExists = true;
    const hostElement = this.fileUploadComp?.el?.nativeElement;
    const browseButton = hostElement?.querySelector("button");
    this.confirmationService.confirm({
      target: browseButton,
      message: "This file already exists",
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Choose New File",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "View File",
      },
      accept: () => {
        if (this.destroyed) {
          return;
        }
        this.closeDialog.emit();
        void this.router.navigate(["repayments"], {
          queryParams: {
            sourceFileId: existingFileID,
            merchants: null,
            terminals: null,
            cashAdvances: null,
            showAll: false,
          },
          queryParamsHandling: "merge",
        });
      },
      reject: () => {
        if (this.destroyed) {
          return;
        }
        this.fileUploadComp?.clear();
        this.fileName = "";
        this.fileExists = false;
        const fileInput: HTMLInputElement | null =
          hostElement?.querySelector('input[type="file"]');

        fileInput?.click();
      },
    });
  }

  handleCloseDialog() {
    if (this.destroyed) {
      return;
    }
    this.fileUploadComp?.clear();
    this.fileName = "";
    this.isLoading = false;
    this.closeDialog.emit();
  }

  private buildWorkbookReadOptions(
    type: "array" | "binary" | "base64" = "array",
    provider: PaymentProvider | null = null,
    password?: string,
  ): XLSX.ParsingOptions {
    const resolvedPassword = password ?? this.resolveWorkbookPassword(provider);
    return resolvedPassword
      ? {
          type,
          password: resolvedPassword,
        }
      : { type };
  }

  private readWorkbook(
    buffer: Uint8Array | string,
    type: "array" | "binary" | "base64",
    provider: PaymentProvider,
    password?: string,
  ): XLSX.WorkBook {
    try {
      const opts = this.buildWorkbookReadOptions(type, provider, password);
      return XLSX.read(buffer, { password: opts.password, type });
    } catch (error) {
      if (
        provider === PaymentProvider.CARDONE &&
        type === "array" &&
        typeof buffer !== "string"
      ) {
        const binary = this.arrayBufferToBinaryString(buffer);
        return XLSX.read(
          binary,
          this.buildWorkbookReadOptions("binary", provider, password),
        );
      }
      if (provider === PaymentProvider.CARDONE && type !== "base64") {
        const base64 =
          typeof buffer === "string" && type === "binary"
            ? btoa(buffer)
            : this.toBase64(buffer as Uint8Array);
        return XLSX.read(
          base64,
          this.buildWorkbookReadOptions("base64", provider, password),
        );
      }
      throw error;
    }
  }

  private tryReadWorkbook(
    inputs: { binaryString?: string; byteArray?: Uint8Array },
    provider: PaymentProvider,
  ): XLSX.WorkBook {
    if (provider !== PaymentProvider.CARDONE) {
      const binarySource =
        inputs.binaryString ||
        (inputs.byteArray
          ? this.arrayBufferToBinaryString(inputs.byteArray)
          : null);
      if (!binarySource) {
        throw new Error("Unable to read workbook");
      }
      return this.readWorkbook(binarySource, "binary", provider);
    }

    const attempts: Array<{
      input: Uint8Array | string;
      type: "array" | "binary" | "base64";
    }> = [];

    const addAttempt = (
      type: "array" | "binary" | "base64",
      input?: Uint8Array | string,
    ) => {
      if (input === undefined || input === null) {
        return;
      }
      attempts.push({ input, type });
    };

    const byteArray = inputs.byteArray;
    const binaryString = inputs.binaryString;

    addAttempt("binary", binaryString);
    addAttempt("array", byteArray);
    if (byteArray) {
      addAttempt("base64", this.toBase64(byteArray));
    }
    if (!attempts.length) {
      throw new Error("No workbook data available");
    }
    let lastError: unknown = null;
    const passwords = this.resolveWorkbookPasswords(provider);
    const passwordAttempts = [...passwords, undefined];
    for (const password of passwordAttempts) {
      for (const attempt of attempts) {
        try {
          return this.readWorkbook(
            attempt.input,
            attempt.type,
            provider,
            password,
          );
        } catch (error) {
          lastError = error;
        }
      }
    }
    if (lastError) {
      throw lastError;
    }
    throw new Error("Unable to read workbook");
  }

  /**
   * Identify a known export before it is sent anywhere. CardOne is the only
   * password-protected export, so it is retried with its known password only
   * after the ordinary workbook read fails.
   */
  private async detectProviderAndReadWorkbook(inputs: {
    binaryString?: string;
    byteArray?: Uint8Array;
  }): Promise<{ provider: PaymentProvider; workBook: XLSX.WorkBook }> {
    try {
      const workBook = this.tryReadWorkbook(inputs, PaymentProvider.APS);
      const provider = this.detectProvider(workBook);
      if (provider) {
        return { provider, workBook };
      }
    } catch {
      // CardOne files are encrypted and are handled below.
    }

    const decryptedBytes = await this.decryptWorkbookIfNeeded(
      inputs.byteArray || null,
      PaymentProvider.CARDONE,
    );
    if (!decryptedBytes) {
      throw new Error("Unable to read workbook");
    }
    const workBook = this.tryReadWorkbook(
      {
        byteArray: decryptedBytes,
        binaryString: this.arrayBufferToBinaryString(decryptedBytes),
      },
      PaymentProvider.CARDONE,
    );
    if (this.detectProvider(workBook) === PaymentProvider.CARDONE) {
      return { provider: PaymentProvider.CARDONE, workBook };
    }
    throw new Error("The workbook does not match a supported upload format");
  }

  private detectProvider(workBook: XLSX.WorkBook): PaymentProvider | null {
    if (
      this.findWorksheetWithHeaders(workBook, [
        "Repayment Date",
        "Description",
        "Amount",
        "Data Source",
        "365 Split",
        "Comments",
        "Terminal",
      ])
    ) {
      return PaymentProvider.CUSTOM;
    }
    if (
      this.findWorksheetWithHeaders(workBook, [
        "Transaction Reference",
        "Transaction Date",
        "Location ID",
        "Beneficiary Sort Code",
        "Beneficiary Account Number",
        "Split Transfer Amount",
        "Split Percentage",
      ])
    ) {
      return PaymentProvider.DOJO_DIRECT_SPLIT;
    }
    if (this.findCardoneWorksheet(workBook)) {
      return PaymentProvider.CARDONE;
    }
    if (
      this.findWorksheetWithHeaders(workBook, [
        "Payment Date",
        "Business Name",
        "APS SortCode",
        "Reference",
        "Amount",
        "Destination Type",
        "Destination SortCode",
      ])
    ) {
      return PaymentProvider.APS;
    }
    if (
      this.findWorksheetWithHeaders(workBook, [
        "LOCAL_DATETIME",
        "TERMLOCATION",
        "TXNBANKSORTCODE",
        "TXNBANKACCOUNTNO",
        "AMTTXN",
      ])
    ) {
      return PaymentProvider.APS;
    }
    if (
      this.findWorksheetWithHeaders(workBook, [
        "Txn Datetime - UTC",
        "Account Alias",
        "Txn Amount",
        "Payer/Payee Name",
      ])
    ) {
      return PaymentProvider.MODULR;
    }

    const providerName = this.findWorkbookText(workBook, [
      "clover - 365 finance",
      "cardnet - 365 finance",
    ]);
    if (providerName === "clover - 365 finance") {
      return PaymentProvider.FDMS;
    }
    if (providerName === "cardnet - 365 finance") {
      return PaymentProvider.CARDNET;
    }

    // GoCardless exports are CSVs with a stable, wide column layout. Require
    // both a GoCardless marker and the columns used by its importer.
    if (
      this.findWorkbookText(workBook, ["gocardless"]) &&
      this.firstWorksheetHasAtLeastColumns(workBook, 18)
    ) {
      return PaymentProvider.GOCARDLESS;
    }
    return null;
  }

  private findWorkbookText(
    workBook: XLSX.WorkBook,
    values: string[],
  ): string | null {
    const targets = values.map((value) => value.toLowerCase());
    for (const sheetName of workBook.SheetNames || []) {
      const worksheet = workBook.Sheets?.[sheetName];
      if (!worksheet || !worksheet["!ref"]) continue;
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      const maxRow = Math.min(range.e.r, range.s.r + 10);
      const maxCol = Math.min(range.e.c, range.s.c + 20);
      for (let row = range.s.r; row <= maxRow; row++) {
        for (let col = range.s.c; col <= maxCol; col++) {
          const value = this.asString(
            worksheet[XLSX.utils.encode_cell({ r: row, c: col })]?.v,
          )
            .trim()
            .toLowerCase();
          const match = targets.find((target) => value.includes(target));
          if (match) return match;
        }
      }
    }
    return null;
  }

  private firstWorksheetHasAtLeastColumns(
    workBook: XLSX.WorkBook,
    minimumColumns: number,
  ): boolean {
    const worksheet = this.getWorksheet(workBook, 0);
    if (!worksheet?.["!ref"]) return false;
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    return range.e.c - range.s.c + 1 >= minimumColumns;
  }

  private ensureBinaryData(
    data: string | ArrayBuffer | null,
  ): Uint8Array | null {
    if (!data) {
      return null;
    }
    if (data instanceof ArrayBuffer) {
      return new Uint8Array(data);
    }
    if (typeof data === "string") {
      const view = new Uint8Array(data.length);
      for (let i = 0; i < data.length; i++) {
        view[i] = data.charCodeAt(i) & 0xff;
      }
      return view;
    }
    return null;
  }

  private async decryptWorkbookIfNeeded(
    data: Uint8Array | null,
    provider: PaymentProvider,
  ): Promise<Uint8Array | null> {
    if (!data) {
      return null;
    }
    if (provider !== PaymentProvider.CARDONE) {
      return data;
    }
    const passwords = this.resolveWorkbookPasswords(provider);
    for (const password of passwords) {
      try {
        const decrypted = await officeCrypto.decrypt(Buffer.from(data), {
          password,
        });
        if (!decrypted) {
          continue;
        }
        if (decrypted instanceof Uint8Array) {
          return decrypted;
        }
        if (decrypted instanceof ArrayBuffer) {
          return new Uint8Array(decrypted);
        }
        if (ArrayBuffer.isView(decrypted)) {
          return new Uint8Array(
            (decrypted as ArrayBufferView).buffer.slice(
              (decrypted as ArrayBufferView).byteOffset,
              (decrypted as ArrayBufferView).byteOffset +
                (decrypted as ArrayBufferView).byteLength,
            ),
          );
        }
        return new Uint8Array(decrypted as ArrayLike<number>);
      } catch {}
    }
    return data;
  }

  private ensureBinaryString(data: string | ArrayBuffer | null): string | null {
    if (!data) {
      return null;
    }
    if (typeof data === "string") {
      return data;
    }
    if (data instanceof ArrayBuffer) {
      return this.arrayBufferToBinaryString(new Uint8Array(data));
    }
    return null;
  }

  private arrayBufferToBinaryString(buffer: ArrayBuffer | Uint8Array): string {
    let binary = "";
    const bytes =
      buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return binary;
  }

  private toBase64(buffer: ArrayBuffer | Uint8Array): string {
    return btoa(this.arrayBufferToBinaryString(buffer));
  }

  private resolveWorkbookPasswords(
    provider: PaymentProvider | null | string,
  ): string[] {
    if (!provider) {
      return [];
    }
    const normalized = provider.toString().trim().toUpperCase();
    if (normalized === PaymentProvider.CARDONE.toUpperCase()) {
      return ["spec365", "Spec365", "SPEC365"];
    }
    return [];
  }

  private resolveWorkbookPassword(
    provider: PaymentProvider | null | string,
  ): string | undefined {
    return this.resolveWorkbookPasswords(provider)[0];
  }

  /**
   * File imports must be reviewed before they become active repayments.
   * Set this on every row instead of relying on the current repayments view.
   */
  private prepareUploadedRepayments(
    parsedRows: ParsedRepaymentRow[],
    sourceFileId: string,
    provider: PaymentProvider,
  ): Repayments {
    return parsedRows.map(({ cash_advance_name, customer_number, ...row }) => ({
      ...row,
      payment_amount: this.normalizeUploadedPaymentAmount(
        row.payment_amount,
        provider,
      ),
      x365_split: this.normalize365SplitPercentage(row.x365_split),
      source_file: sourceFileId,
      country: this.selectedCountry.code,
      data_source:
        provider === PaymentProvider.CUSTOM ? row.data_source : provider,
      staged: true,
    }));
  }

  private normalizeUploadedPaymentAmount(
    value: number | string | undefined,
    provider: PaymentProvider,
  ): number | string | undefined {
    if (
      provider !== PaymentProvider.FDMS &&
      provider !== PaymentProvider.CARDNET
    ) {
      return value;
    }

    const numericValue = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numericValue) ? -numericValue : value;
  }

  private normalize365SplitPercentage(
    value: number | string | undefined,
  ): number | string | undefined {
    if (value === undefined || value === null || value === "") return undefined;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue === 0) return undefined;
    const percentage = Math.abs(numericValue) < 1 ? numericValue * 100 : numericValue;
    return Number(percentage.toFixed(6));
  }

  private parseRepaymentsFromWorkbook(
    workBook: XLSX.WorkBook,
    provider: PaymentProvider,
  ): ParsedRepaymentRow[] {
    switch (provider) {
      case PaymentProvider.APS:
        return this.parseApsRows(workBook);
      case PaymentProvider.CARDONE:
        return this.parseCardoneRows(workBook);
      case PaymentProvider.CARDNET:
        return this.parseCardnetOrFdmsRows(workBook);
      case PaymentProvider.FDMS:
        return this.parseCardnetOrFdmsRows(workBook);
      case PaymentProvider.GOCARDLESS:
        return this.parseGocardlessRows(workBook);
      case PaymentProvider.DOJO_DIRECT_SPLIT:
        return this.parseDojoDirectSplitRows(workBook);
      case PaymentProvider.MODULR:
        return this.parseModulrRows(workBook);
      case PaymentProvider.CUSTOM:
        return this.parseCustomRows(workBook);
      default:
        return [];
    }
  }

  private parseCustomRows(workBook: XLSX.WorkBook): ParsedRepaymentRow[] {
    const worksheet = this.getWorksheet(workBook, 0);
    if (!worksheet) {
      return [];
    }
    const { header, rows } = this.extractRows(worksheet, 0);
    const columnIndexes = new Map<keyof ParsedRepaymentRow, number>();
    header.forEach((column, index) => {
      const normalized = this.normalizeHeaderName(column);
      const mappedColumn = CUSTOM_TEMPLATE_COLUMNS[normalized];
      if (mappedColumn) {
        columnIndexes.set(mappedColumn, index);
      }
    });
    const missingHeaders = Object.keys(REQUIRED_CUSTOM_TEMPLATE_COLUMNS).filter(
      (header) =>
        !Array.from(columnIndexes.keys()).includes(
          REQUIRED_CUSTOM_TEMPLATE_COLUMNS[header],
        ),
    );
    if (
      !columnIndexes.has("terminal_name") &&
      !columnIndexes.has("terminal_id")
    ) {
      missingHeaders.push("terminal (name) or terminal (id)");
    }
    if (missingHeaders.length) {
      this.notifyError(
        "Invalid custom template",
        `Use the downloaded template. Missing columns: ${missingHeaders.join(", ")}.`,
      );
      return [];
    }

    return rows
      .map((row) => this.mapCustomRepaymentRow(row, columnIndexes))
      .filter((row): row is ParsedRepaymentRow => row !== null);
  }

  private mapCustomRepaymentRow(
    row: SpreadsheetRow,
    columnIndexes: Map<keyof ParsedRepaymentRow, number>,
  ): ParsedRepaymentRow | null {
    const repayment: Partial<Repayment> = {};
    let hasValues = false;
    const numericColumns = new Set(["payment_amount", "x365_split"]);
    const dateColumns = new Set(["repayment_date"]);

    columnIndexes.forEach((index, column) => {
      const value = this.getColumnValueByIndex(row, index);
      if (
        value === null ||
        value === undefined ||
        this.asString(value) === ""
      ) {
        return;
      }
      hasValues = true;
      if (numericColumns.has(column)) {
        const parsed = this.parseNumber(value);
        if (parsed !== null) {
          (repayment as any)[column] = parsed;
        }
        return;
      }
      if (dateColumns.has(column)) {
        const parsed = this.parseDateValue(this.getDateColumnValue(row, index));
        if (parsed) {
          (repayment as any)[column] = this.toDatabaseDate(parsed);
        }
        return;
      }
      (repayment as any)[column] = this.asString(value);
    });

    return hasValues
      ? {
          ...repayment,
          row_number:
            (repayment.row_number as number | undefined) ?? row.rowNumber,
        }
      : null;
  }

  private async validateAndPrepareCustomRows(
    rows: ParsedRepaymentRow[],
  ): Promise<ParsedRepaymentRow[]> {
    const errors: string[] = [];
    const terminalIds = new Map<string, string>();
    const cashAdvanceIds = new Map<string, string>();
    const merchantIds = new Map<string, string>();
    const resolveName = async (
      name: string,
      type: "Terminal" | "Cash Advance",
    ): Promise<string | null> => {
      const cache = type === "Terminal" ? terminalIds : cashAdvanceIds;
      if (cache.has(name)) {
        return cache.get(name) ?? null;
      }
      const matches =
        type === "Terminal"
          ? await this.repaymentsService.findTerminalsByExactName(name, {
              signal: this.lifecycleAbort.signal,
            })
          : await this.repaymentsService.findCashAdvancesByExactName(name, {
              signal: this.lifecycleAbort.signal,
            });
      if (matches.length !== 1 || !matches[0]?.id) {
        cache.set(name, "");
        return null;
      }
      cache.set(name, matches[0].id);
      return matches[0].id;
    };
    const resolveCustomerNumber = async (
      customerNumber: string,
    ): Promise<string | null> => {
      if (merchantIds.has(customerNumber)) {
        return merchantIds.get(customerNumber) ?? null;
      }
      const matches = await this.repaymentsService.findMerchantsByCustomerNumber(
        customerNumber,
        { signal: this.lifecycleAbort.signal },
      );
      if (matches.length !== 1 || !matches[0]?.id) {
        merchantIds.set(customerNumber, "");
        return null;
      }
      merchantIds.set(customerNumber, matches[0].id);
      return matches[0].id;
    };

    const prepared: ParsedRepaymentRow[] = [];
    for (const row of rows) {
      const rowErrors: string[] = [];
      if (!row.repayment_date) {
        rowErrors.push("Repayment Date must be a valid date.");
      }
      if (!row.description) {
        rowErrors.push("Description is required.");
      } else if (
        !REPAYMENT_DESCRIPTION_OPTIONS.includes(row.description as any)
      ) {
        rowErrors.push(
          `Description "${row.description}" is not an allowed value.`,
        );
      }
      if (
        row.payment_amount === undefined ||
        row.payment_amount === null ||
        typeof row.payment_amount !== "number"
      ) {
        rowErrors.push("Amount must be a number.");
      }
      if (!row.data_source) {
        rowErrors.push("Data Source is required.");
      } else if (
        !REPAYMENT_DATA_SOURCE_OPTIONS.includes(row.data_source as any)
      ) {
        rowErrors.push(
          `Data Source "${row.data_source}" is not an allowed value.`,
        );
      }
      if (
        row.x365_split !== undefined &&
        row.x365_split !== null &&
        typeof row.x365_split !== "number"
      ) {
        rowErrors.push("365 Split must be a number when supplied.");
      }

      const customerNumber = row.customer_number?.trim();
      if (customerNumber) {
        const merchantId = await resolveCustomerNumber(customerNumber);
        if (merchantId) {
          row.merchant_id = merchantId;
        } else {
          rowErrors.push(
            `Customer Number "${customerNumber}" was not found or is not unique.`,
          );
        }
      }

      const terminalName = row.terminal_name?.trim();
      const terminalId = row.terminal_id?.trim();
      const terminalRequired = isTerminalRequired(
        row.description,
        row.data_source,
      );
      if (
        !terminalName &&
        !terminalId &&
        terminalRequired
      ) {
        rowErrors.push("Terminal is required.");
      } else if (!terminalId && terminalName) {
        const resolvedTerminalId = await resolveName(terminalName, "Terminal");
        if (resolvedTerminalId) {
          row.terminal_id = resolvedTerminalId;
        } else {
          rowErrors.push(
            `Terminal "${terminalName}" was not found or is not unique. Use the exact terminal name shown in the UI or a terminal ID.`,
          );
        }
      }
      const cashAdvanceName = row.cash_advance_name?.trim();
      const cashAdvanceId = row.cash_advance_id?.trim();
      if ((cashAdvanceName || cashAdvanceId) && terminalRequired) {
        rowErrors.push(
          "Cash Advance can only be supplied for repayments whose type does not require a terminal.",
        );
      } else if (!cashAdvanceId && cashAdvanceName) {
        const resolvedCashAdvanceId = await resolveName(
          cashAdvanceName,
          "Cash Advance",
        );
        if (resolvedCashAdvanceId) {
          row.cash_advance_id = resolvedCashAdvanceId;
        } else {
          rowErrors.push(
            `Cash Advance "${cashAdvanceName}" was not found or is not unique. Use the exact cash advance name shown in the UI or a cash advance ID.`,
          );
        }
      }
      if (rowErrors.length) {
        errors.push(`Row ${row.row_number}: ${rowErrors.join(" ")}`);
      } else {
        prepared.push(row);
      }
    }

    if (errors.length) {
      const additional =
        errors.length > 8 ? ` (+${errors.length - 8} more)` : "";
      this.notifyError(
        "Custom upload needs changes",
        `${errors.slice(0, 8).join(" ")}${additional}`,
      );
      return [];
    }
    return prepared;
  }

  private parseApsRows(workBook: XLSX.WorkBook): ParsedRepaymentRow[] {
    const transactionExtract = this.findWorksheetWithHeaders(workBook, [
      "LOCAL_DATETIME",
      "TERMLOCATION",
      "TXNBANKSORTCODE",
      "TXNBANKACCOUNTNO",
      "AMTTXN",
    ]);
    if (transactionExtract) {
      return this.parseApsTransactionExtractRows(
        transactionExtract.worksheet,
        transactionExtract.headerRowIndex,
      );
    }

    const worksheet = this.getWorksheet(workBook, 0);
    if (!worksheet) {
      return [];
    }
    const { rows } = this.extractRows(worksheet, 0);
    const parsed: Array<ParsedRepaymentRow | null> = rows
      .filter((row) => {
        const destinationType = this.asString(
          this.getColumnValue(row, "G"),
        )
          .trim()
          .toUpperCase();
        return destinationType === "MCA PROVIDER";
      })
      .map((row) => {
        const payment_amount = this.parseNumber(this.getColumnValue(row, "F"));
        const repaymentDate = this.parseDateValue(
          this.getDateColumnValue(row, this.columnIndex("A")),
        );
        if (payment_amount === null || !repaymentDate) {
          return null;
        }
        const reference = this.asString(this.getColumnValue(row, "E"));
        const repayment: ParsedRepaymentRow = {
          description: "Daily Repayment",
          repayment_date: repaymentDate,
          payment_amount: payment_amount,
          beneficiary_sort_code: this.normalizeSortCode(
            this.getColumnValue(row, "H"),
          ),
          terminal_name: reference || undefined,
          row_number: row.rowNumber,
        };
        return repayment;
      });
    return parsed.filter((row): row is ParsedRepaymentRow => row !== null);
  }

  /**
   * APS also provides a tab-delimited transaction extract. Repayment entries
   * identify a terminal by the 365BusinessFinance prefix; beneficiary details
   * live on the corresponding non-365 merchant transaction in the same file.
   */
  private parseApsTransactionExtractRows(
    worksheet: XLSX.WorkSheet,
    headerRowIndex: number,
  ): ParsedRepaymentRow[] {
    const { header, rows } = this.extractRows(worksheet, headerRowIndex);
    const localDateTimeIndex = this.getHeaderIndex(header, ["LOCAL_DATETIME"]);
    const terminalLocationIndex = this.getHeaderIndex(header, ["TERMLOCATION"]);
    const sortCodeIndex = this.getHeaderIndex(header, ["TXNBANKSORTCODE"]);
    const accountNumberIndex = this.getHeaderIndex(header, ["TXNBANKACCOUNTNO"]);
    const amountIndex = this.getHeaderIndex(header, ["AMTTXN"]);
    if (
      localDateTimeIndex === null ||
      terminalLocationIndex === null ||
      sortCodeIndex === null ||
      accountNumberIndex === null ||
      amountIndex === null
    ) {
      return [];
    }

    const prefix = "365BusinessFinance ";
    const accountDetailsByTerminal = new Map<
      string,
      { sortCode: string | null; accountNumber: string | null }
    >();

    for (const row of rows) {
      const location = this.asString(
        this.getColumnValueByIndex(row, terminalLocationIndex),
      ).trim();
      if (!location || location.startsWith(prefix)) {
        continue;
      }
      const terminalName = location.split(/\s+/).pop();
      if (!terminalName || accountDetailsByTerminal.has(terminalName)) {
        continue;
      }
      const sortCode = this.normalizeSortCode(
        this.getColumnValueByIndex(row, sortCodeIndex),
      );
      const accountNumber = this.asString(
        this.getColumnValueByIndex(row, accountNumberIndex),
      ).trim();
      accountDetailsByTerminal.set(terminalName, {
        sortCode: sortCode || null,
        accountNumber: accountNumber || null,
      });
    }

    const parsed: Array<ParsedRepaymentRow | null> = rows.map((row) => {
      const location = this.asString(
        this.getColumnValueByIndex(row, terminalLocationIndex),
      ).trim();
      if (!location.startsWith(prefix)) {
        return null;
      }
      const terminalName = location.slice(prefix.length).trim();
      const transactionAmount = this.parseNumber(
        this.getColumnValueByIndex(row, amountIndex),
      );
      const repaymentDate = this.parseApsTransactionExtractDate(
        this.getDateColumnValue(row, localDateTimeIndex),
      );
      if (!terminalName || transactionAmount === null || !repaymentDate) {
        return null;
      }
      const accountDetails = accountDetailsByTerminal.get(terminalName);
      return {
        description: "Daily Repayment",
        repayment_date: repaymentDate,
        // APS records repayments as debits; repayment imports use the
        // opposite sign, so a -5.86 transaction becomes a 5.86 repayment.
        payment_amount: -transactionAmount,
        beneficiary_sort_code: accountDetails?.sortCode ?? null,
        beneficiary_account_number: accountDetails?.accountNumber ?? null,
        terminal_name: terminalName,
        row_number: row.rowNumber,
      };
    });
    return parsed.filter((row): row is ParsedRepaymentRow => row !== null);
  }

  /**
   * LOCAL_DATETIME is always year/month/day. Format it directly for the
   * database so neither the browser nor the API can swap day and month.
   */
  private parseApsTransactionExtractDate(value: any): string | null {
    // SheetJS detects LOCAL_DATETIME as a date when reading the tab-delimited
    // extract. Its displayed value is locale-dependent (for example 9/2/26),
    // but its raw numeric value preserves the correct date.
    if (typeof value === "number" || value instanceof Date) {
      const parsed = this.parseDateValue(value);
      return parsed ? this.toDatabaseDate(parsed) : null;
    }
    const match = this.asString(value)
      .trim()
      .match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:\s|$)/);
    if (!match) {
      return null;
    }
    const [, year, month, day] = match;
    const date = this.createValidUtcDate(
      Number(year),
      Number(month),
      Number(day),
    );
    return date ? this.toDatabaseDate(date.toISOString()) : null;
  }

  private parseCardoneRows(workBook: XLSX.WorkBook): ParsedRepaymentRow[] {
    const sheetInfo = this.findCardoneWorksheet(workBook);
    if (!sheetInfo) {
      return [];
    }
    const { worksheet, headerRowIndex } = sheetInfo;
    const { header, rows } = this.extractRows(worksheet, headerRowIndex);
    const transactionDateIndex = this.getHeaderIndex(header, [
      "Transaction Date",
    ]);
    const customerNameIndex = this.getHeaderIndex(header, ["Customer Name"]);
    const labelIndex = this.getHeaderIndex(header, ["Transaction Label"]);
    const sortCodeIndex = this.getHeaderIndex(header, [
      "Beneficiary_Sort_Code__c",
      "Customer Debit Bene Sort Code",
      "Sort Code",
    ]);
    const accountNumberIndex = this.getHeaderIndex(header, [
      "Beneficiary_Account_Number__c",
      "Customer Debit Bene Acc Num",
      "Account Number",
    ]);
    const amountIndex = this.getHeaderIndex(header, ["365 Debit"]);
    console.log("Cardone indices", {
      transactionDateIndex,
      customerNameIndex,
      labelIndex,
      sortCodeIndex,
      accountNumberIndex,
      amountIndex,
    });
    const parsed: Array<ParsedRepaymentRow | null> = rows.map((row) => {
      const payment_amount = this.parseNumber(
        this.getColumnValueByIndex(row, amountIndex),
      );
      const repaymentDate = this.parseDateValue(
        this.getDateColumnValue(row, transactionDateIndex ?? -1),
      );
      if (payment_amount === null || !repaymentDate) {
        return null;
      }
      const repayment: ParsedRepaymentRow = {
        description: "Daily Repayment",
        repayment_date: repaymentDate,
        payment_amount: payment_amount,
        beneficiary_sort_code: this.normalizeSortCode(
          this.getColumnValueByIndex(row, sortCodeIndex),
        ),
        beneficiary_account_number:
          this.asString(this.getColumnValueByIndex(row, accountNumberIndex)) ||
          undefined,
        terminal_name:
          this.asString(this.getColumnValueByIndex(row, customerNameIndex)) ||
          undefined,
        row_number: row.rowNumber,
      };
      console.log(this.asString(this.getColumnValueByIndex(row, labelIndex)));
      return repayment;
    });
    return parsed.filter((row): row is ParsedRepaymentRow => row !== null);
  }

  /**
   * CardOne's latest export replaces the legacy Sort Code and Account Number
   * columns with beneficiary columns. The remaining headers uniquely identify
   * the export, while parsing below selects whichever column set is present.
   */
  private findCardoneWorksheet(
    workBook: XLSX.WorkBook,
  ): { worksheet: XLSX.WorkSheet; headerRowIndex: number } | null {
    return this.findWorksheetWithHeaders(workBook, [
      "Transaction Date",
      "Customer Name",
      "Transaction Label",
      "365 Debit",
    ]);
  }

  private parseCardnetOrFdmsRows(
    workBook: XLSX.WorkBook,
  ): ParsedRepaymentRow[] {
    const worksheet = this.getWorksheet(workBook, 0);
    if (!worksheet) {
      return [];
    }
    const repaymentDate =
      this.resolveCardnetOrFdmsDate(workBook) || new Date().toISOString();
    const { header, rows } = this.extractRows(worksheet, 5);
    const merchantNameIndex = this.getHeaderIndex(header, [
      "Merchant Name",
      "Merchant",
    ]);
    const merchantNumberIndex = this.getHeaderIndex(header, [
      "Merchant Number",
      "Number",
    ]);
    const thirdPartyIndex = this.getHeaderIndex(header, ["Third Party ID"]);
    const amountIndex = this.getHeaderIndex(header, [
      "Funding Amount",
      "Funding Amount (£)",
      "Funding Amount (GBP)",
    ]);
    const parsed: Array<ParsedRepaymentRow | null> = rows.map((row) => {
      const amountCell =
        amountIndex !== null
          ? this.getColumnValueByIndex(row, amountIndex)
          : this.getColumnValue(row, "E");
      const payment_amount = this.parseNumber(amountCell);
      if (payment_amount === null) {
        return null;
      }
      const merchantNameValue =
        merchantNameIndex !== null
          ? this.getColumnValueByIndex(row, merchantNameIndex)
          : this.getColumnValue(row, "C");
      const merchantName = this.asString(merchantNameValue);
      const terminalNameValue =
        merchantNumberIndex !== null
          ? this.getColumnValueByIndex(row, merchantNumberIndex)
          : this.getColumnValue(row, "B");
      const terminalName = this.asString(terminalNameValue) || merchantName;
      // const description =
      //   merchantName ||
      //   terminalName ||
      //   this.asString(
      //     thirdPartyIndex !== null
      //       ? this.getColumnValueByIndex(row, thirdPartyIndex)
      //       : this.getColumnValue(row, 'D'),
      //   )
      const repayment: ParsedRepaymentRow = {
        description: "Daily Repayment",
        repayment_date: repaymentDate,
        payment_amount,
        terminal_name: terminalName || undefined,
        row_number: row.rowNumber,
      };
      return repayment;
    });
    return parsed.filter((row): row is ParsedRepaymentRow => row !== null);
  }

  private parseGocardlessRows(workBook: XLSX.WorkBook): ParsedRepaymentRow[] {
    const worksheet = this.getWorksheet(workBook, 0);
    if (!worksheet) {
      return [];
    }
    const { rows } = this.extractRows(worksheet, 0);
    const parsed: Array<ParsedRepaymentRow | null> = rows.map((row) => {
      // GoCardless payout breakdown export:
      // D = terminal name, J = resource status, L = gross amount, R = payout
      // date, AD = Salesforce ID. Exclude Salesforce-mapped paid resources;
      // resource-status matching is case-insensitive.
      const salesforceId = this.asString(this.getColumnValue(row, "AD")).trim();
      const resourceStatus = this.asString(this.getColumnValue(row, "J"))
        .trim()
        .toLowerCase();
      if (salesforceId && resourceStatus === "paid") {
        return null;
      }

      const payment_amount = this.parseNumber(this.getColumnValue(row, "L"));
      const repaymentDate = this.parseGocardlessDate(
        this.getDateColumnValue(row, this.columnIndex("R")),
      );
      if (payment_amount === null || !repaymentDate) {
        return null;
      }
      const terminalName = this.asString(this.getColumnValue(row, "D"));
      // const description =
      //   this.asString(this.getColumnValue(row, 'G')) ||
      //   this.asString(this.getColumnValue(row, 'K')) ||
      //   terminalName
      const repayment: ParsedRepaymentRow = {
        description: "Daily Repayment",
        repayment_date: repaymentDate,
        payment_amount,
        terminal_name: terminalName || undefined,
        row_number: row.rowNumber,
      };
      return repayment;
    });
    return parsed.filter((row): row is ParsedRepaymentRow => row !== null);
  }

  /**
   * A Dojo direct-split settlement has one or more rows per transaction
   * reference and settlement destination. The repayment's beneficiary is the
   * non-365 destination, while the 365 destination (account 39464860)
   * supplies the repayment amount and split percentage. Dojo can reuse a
   * transaction reference for different locations, so the location and, when
   * present, customer reference are included in the grouping key.
   */
  private parseDojoDirectSplitRows(
    workBook: XLSX.WorkBook,
  ): ParsedRepaymentRow[] {
    const worksheetInfo = this.findWorksheetWithHeaders(workBook, [
      "Transaction Reference",
      "Transaction Date",
      "Location ID",
      "Beneficiary Sort Code",
      "Beneficiary Account Number",
      "Split Transfer Amount",
      "Split Percentage",
    ]);
    if (!worksheetInfo) {
      return [];
    }

    const { header, rows } = this.extractRows(
      worksheetInfo.worksheet,
      worksheetInfo.headerRowIndex,
    );
    const transactionReferenceIndex = this.getHeaderIndex(header, [
      "Transaction Reference",
    ]);
    const transactionDateIndex = this.getHeaderIndex(header, [
      "Transaction Date",
    ]);
    const locationIdIndex = this.getHeaderIndex(header, ["Location ID"]);
    const customerReferenceIndex = this.getHeaderIndex(header, [
      "Customer Reference",
    ]);
    const sortCodeIndex = this.getHeaderIndex(header, [
      "Beneficiary Sort Code",
    ]);
    const accountNumberIndex = this.getHeaderIndex(header, [
      "Beneficiary Account Number",
    ]);
    const splitAmountIndex = this.getHeaderIndex(header, [
      "Split Transfer Amount",
    ]);
    const splitPercentageIndex = this.getHeaderIndex(header, [
      "Split Percentage",
    ]);
    if (
      transactionReferenceIndex === null ||
      transactionDateIndex === null ||
      locationIdIndex === null ||
      sortCodeIndex === null ||
      accountNumberIndex === null ||
      splitAmountIndex === null ||
      splitPercentageIndex === null
    ) {
      return [];
    }

    const groups = new Map<string, SpreadsheetRow[]>();
    for (const row of rows) {
      const transactionReference = this.asString(
        this.getColumnValueByIndex(row, transactionReferenceIndex),
      );
      if (!transactionReference) {
        continue;
      }
      const locationId = this.asString(
        this.getRawColumnValueByIndex(row, locationIdIndex),
      );
      const customerReference =
        customerReferenceIndex === null
          ? ""
          : this.asString(
              this.getColumnValueByIndex(row, customerReferenceIndex),
            );
      const groupKey = JSON.stringify([
        transactionReference,
        locationId,
        customerReference,
      ]);
      const group = groups.get(groupKey) ?? [];
      group.push(row);
      groups.set(groupKey, group);
    }

    const repaymentRows: ParsedRepaymentRow[] = [];
    for (const group of groups.values()) {
      const beneficiaryRows = group.filter(
        (row) =>
          this.asString(
            this.getColumnValueByIndex(row, accountNumberIndex),
          ) !== "39464860",
      );
      const splitRows = group.filter(
        (row) =>
          this.asString(
            this.getColumnValueByIndex(row, accountNumberIndex),
          ) === "39464860",
      );
      const pairCount = Math.min(beneficiaryRows.length, splitRows.length);

      for (let pairIndex = 0; pairIndex < pairCount; pairIndex += 1) {
        const beneficiaryRow = beneficiaryRows[pairIndex];
        const splitRow = splitRows[pairIndex];

        const paymentAmount = this.parseNumber(
          this.getColumnValueByIndex(splitRow, splitAmountIndex),
        );
        const splitPercentage = this.parseNumber(
          this.getColumnValueByIndex(splitRow, splitPercentageIndex),
        );
        const repaymentDate = this.parseDateValue(
          this.getDateColumnValue(beneficiaryRow, transactionDateIndex),
        );
        const terminalName = this.asString(
          this.getRawColumnValueByIndex(beneficiaryRow, locationIdIndex),
        );
        if (
          paymentAmount === null ||
          splitPercentage === null ||
          !repaymentDate ||
          !terminalName
        ) {
          continue;
        }

        repaymentRows.push({
          description: "Daily Repayment",
          repayment_date: this.toDatabaseDate(repaymentDate),
          terminal_name: terminalName,
          beneficiary_sort_code: this.normalizeSortCode(
            this.getColumnValueByIndex(beneficiaryRow, sortCodeIndex),
          ),
          beneficiary_account_number:
            this.asString(
              this.getColumnValueByIndex(beneficiaryRow, accountNumberIndex),
            ) || null,
          payment_amount: paymentAmount,
          x365_split: splitPercentage,
          row_number: beneficiaryRow.rowNumber,
        });
      }
    }
    return repaymentRows;
  }

  private parseModulrRows(workBook: XLSX.WorkBook): ParsedRepaymentRow[] {
    const worksheet = this.getWorksheet(workBook, 0);
    if (!worksheet) {
      return [];
    }
    const { rows } = this.extractRows(worksheet, 0);
    const parsed: Array<ParsedRepaymentRow | null> = rows.map((row) => {
      const payerPayeeName = this.asString(this.getColumnValue(row, "K"))
        .trim()
        .toLowerCase();
      if (!payerPayeeName.startsWith("365 business")) {
        return null;
      }

      const payment_amount = this.parseNumber(this.getColumnValue(row, "G"));
      const repaymentDate = this.parseDateValue(
        this.getDateColumnValue(row, this.columnIndex("B")),
      );
      if (payment_amount === null || !repaymentDate) {
        return null;
      }

      return {
        description: "Daily Repayment",
        repayment_date: repaymentDate,
        payment_amount,
        terminal_name:
          this.asString(this.getColumnValue(row, "D")) || undefined,
        row_number: row.rowNumber,
      };
    });
    return parsed.filter((row): row is ParsedRepaymentRow => row !== null);
  }

  private resolveCardnetOrFdmsDate(workBook: XLSX.WorkBook): string | null {
    const primarySheet = this.getWorksheet(workBook, 0);
    const directValue = this.parseDateValue(
      this.getCellValue(primarySheet, "G4"),
    );
    if (directValue) {
      return directValue;
    }

    const generationSheet = this.getWorksheetByName(workBook, "Generation");
    if (generationSheet) {
      const generationValue = this.parseDateValue(
        this.getCellValue(generationSheet, "H4"),
      );
      if (generationValue) {
        return generationValue;
      }
    }

    const headerDate = this.findDateInWorksheet(primarySheet);
    if (headerDate) {
      return headerDate;
    }

    const propsDate = this.parseDateFromWorkbookProps(workBook);
    if (propsDate) {
      return propsDate;
    }

    const fileNameDate = this.parseDateFromFileName(this.fileName);
    if (fileNameDate) {
      return fileNameDate;
    }

    this.notifyError(
      "Unable to find settlement date",
      "Repayment date will default to today. Please verify after upload.",
    );
    return new Date().toISOString();
  }

  private getWorksheet(
    workBook: XLSX.WorkBook,
    sheetIndex: number,
  ): XLSX.WorkSheet | null {
    const sheetName = workBook.SheetNames?.[sheetIndex];
    if (!sheetName) {
      return null;
    }
    return workBook.Sheets?.[sheetName] || null;
  }

  private getWorksheetByName(
    workBook: XLSX.WorkBook,
    sheetName: string,
  ): XLSX.WorkSheet | null {
    if (!sheetName) {
      return null;
    }
    const normalized = sheetName.trim().toLowerCase();
    const match = workBook.SheetNames?.find(
      (name) => name && name.trim().toLowerCase() === normalized,
    );
    if (!match) {
      return null;
    }
    return workBook.Sheets?.[match] || null;
  }

  private findWorksheetWithHeaders(
    workBook: XLSX.WorkBook,
    requiredHeaders: string[],
    scanLimit = 50,
  ): { worksheet: XLSX.WorkSheet; headerRowIndex: number } | null {
    if (!workBook?.SheetNames?.length) {
      return null;
    }
    for (let i = 0; i < workBook.SheetNames.length; i++) {
      const worksheet = this.getWorksheet(workBook, i);
      if (!worksheet) {
        continue;
      }
      const headerRowIndex = this.findHeaderRowIndex(
        worksheet,
        requiredHeaders,
        scanLimit,
      );
      if (headerRowIndex !== null) {
        return { worksheet, headerRowIndex };
      }
    }
    return null;
  }

  private findHeaderRowIndex(
    worksheet: XLSX.WorkSheet,
    requiredHeaders: string[],
    scanLimit = 50,
  ): number | null {
    if (!worksheet || !worksheet["!ref"]) {
      return null;
    }
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    const maxRow = Math.min(range.e.r, range.s.r + scanLimit);
    const maxCol = Math.min(range.e.c, range.s.c + 50);
    const normalizedRequired = requiredHeaders.map((header) =>
      this.normalizeHeaderName(header),
    );
    for (let row = range.s.r; row <= maxRow; row++) {
      const normalizedRow: string[] = [];
      for (let col = range.s.c; col <= maxCol; col++) {
        const address = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[address];
        const value = this.asString(cell?.v ?? cell?.w ?? "");
        normalizedRow.push(this.normalizeHeaderName(value));
      }
      const hasAllHeaders = normalizedRequired.every((header) =>
        normalizedRow.includes(header),
      );
      if (hasAllHeaders) {
        return row;
      }
    }
    return null;
  }

  private extractRows(
    worksheet: XLSX.WorkSheet,
    headerRowIndex: number,
  ): ExtractedRows {
    if (!worksheet || !worksheet["!ref"]) {
      return { header: [], rows: [], headerRowIndex };
    }
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    if (headerRowIndex > range.e.r) {
      return { header: [], rows: [], headerRowIndex };
    }
    range.s.r = headerRowIndex;
    const rawRows = XLSX.utils.sheet_to_json<any[]>(worksheet, {
      header: 1,
      raw: false,
      defval: "",
      range,
      blankrows: true,
    }) as any[][];
    if (!rawRows || rawRows.length === 0) {
      return { header: [], rows: [], headerRowIndex };
    }
    const header = (rawRows[0] || []).map((cell) => this.asString(cell));
    if (rawRows.length === 1) {
      return { header, rows: [], headerRowIndex };
    }
    const rows = rawRows.slice(1).map((values, index) => {
      const worksheetRow = headerRowIndex + index + 1;
      const rawValues = values.map((displayValue, columnIndex) => {
        const address = XLSX.utils.encode_cell({
          r: worksheetRow,
          c: range.s.c + columnIndex,
        });
        return worksheet[address]?.v ?? displayValue;
      });
      return {
        values,
        rawValues,
        rowNumber: headerRowIndex + index + 2,
      };
    });
    return { header, rows, headerRowIndex };
  }

  private getColumnValue(row: SpreadsheetRow, column: string): any {
    const index = this.columnIndex(column);
    return row.values?.[index] ?? "";
  }

  private getDateColumnValue(row: SpreadsheetRow, index: number): any {
    return row.rawValues?.[index] ?? row.values?.[index] ?? "";
  }

  private columnIndex(column: string): number {
    const letters = column.trim().toUpperCase();
    let index = 0;
    for (let i = 0; i < letters.length; i++) {
      index = index * 26 + (letters.charCodeAt(i) - 64);
    }
    return Math.max(index - 1, 0);
  }

  private getHeaderIndex(
    header: string[],
    possibleNames: string[],
  ): number | null {
    if (!header || !header.length) {
      return null;
    }
    const normalizedHeader = header.map((name) =>
      this.normalizeHeaderName(name),
    );
    for (const candidate of possibleNames) {
      const normalizedCandidate = this.normalizeHeaderName(candidate);
      const idx = normalizedHeader.findIndex(
        (value) => value === normalizedCandidate,
      );
      if (idx >= 0) {
        return idx;
      }
    }
    return null;
  }

  private normalizeHeaderName(value: string): string {
    if (!value) {
      return "";
    }
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // The downloaded custom template labels this column with its required
    // display format. Keep accepting earlier templates that used the shorter
    // "Repayment Date" heading too.
    switch (normalized) {
      case "repayment date dd mm yyyy":
        return "repayment date";
      case "terminal name":
        return "terminal";
      case "cash advance name":
        return "cash advance";
      default:
        return normalized;
    }
  }

  private getColumnValueByIndex(
    row: SpreadsheetRow,
    index: number | null,
  ): any {
    if (index === null || index === undefined || index < 0) {
      return "";
    }
    return row.values?.[index] ?? "";
  }

  private getRawColumnValueByIndex(
    row: SpreadsheetRow,
    index: number | null,
  ): any {
    if (index === null || index === undefined || index < 0) {
      return "";
    }
    return row.rawValues?.[index] ?? row.values?.[index] ?? "";
  }

  private parseNumber(value: any): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    if (typeof value === "number") {
      return isNaN(value) ? null : value;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      const parenNegative = /^\(.*\)$/.test(trimmed);
      let normalized = trimmed
        .replace(/[^\d.,\-]/g, "")
        .replace(/£/g, "")
        .replace(/,/g, "");
      if (!normalized) {
        return null;
      }

      let trailingNegative = false;
      if (
        normalized.endsWith("-") &&
        normalized.indexOf("-", 0) === normalized.length - 1
      ) {
        trailingNegative = true;
        normalized = normalized.slice(0, -1);
      }

      if (!normalized) {
        return null;
      }

      const parsed = Number(normalized);
      if (isNaN(parsed)) {
        return null;
      }

      if (parenNegative || trailingNegative) {
        return -Math.abs(parsed);
      }
      return parsed;
    }
    return null;
  }

  private parseBoolean(value: any): boolean | null {
    if (typeof value === "boolean") {
      return value;
    }
    const normalized = this.asString(value).toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "n"].includes(normalized)) {
      return false;
    }
    return null;
  }

  private parseDateValue(value: any): string | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (
      typeof value === "number" &&
      XLSX.SSF &&
      typeof XLSX.SSF.parse_date_code === "function"
    ) {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (parsed) {
        const date = new Date(
          Date.UTC(
            parsed.y,
            (parsed.m || 1) - 1,
            parsed.d || 1,
            parsed.H || 0,
            parsed.M || 0,
            Math.floor(parsed.S || 0),
          ),
        );
        return date.toISOString();
      }
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const dashMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (dashMatch) {
        const [, year, month, day] = dashMatch;
        const date = this.createValidUtcDate(
          Number(year),
          Number(month),
          Number(day),
        );
        if (date) {
          return date.toISOString();
        }
      }
      const yearFirstSlashMatch = trimmed.match(
        /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/,
      );
      if (yearFirstSlashMatch) {
        const [, year, month, day] = yearFirstSlashMatch;
        const date = this.createValidUtcDate(
          Number(year),
          Number(month),
          Number(day),
        );
        if (date) {
          return date.toISOString();
        }
      }
      const slashMatch = trimmed.match(
        /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/,
      );
      if (slashMatch) {
        const [, day, month, year] = slashMatch;
        const numericYear =
          year.length === 2 ? Number(`20${year}`) : Number(year);
        const date = this.createValidUtcDate(
          numericYear,
          Number(month),
          Number(day),
        );
        if (date) {
          return date.toISOString();
        }
      }
      const parsedDate = new Date(trimmed);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString();
      }
    }
    return null;
  }

  /**
   * GoCardless CSV payouts use the US month/day/year display format. Keep this
   * separate from the general parser, whose slash-formatted values are treated
   * as day/month/year for the custom upload template.
   */
  private parseGocardlessDate(value: any): string | null {
    if (typeof value === "string") {
      const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (match) {
        const [, month, day, year] = match;
        const numericYear =
          year.length === 2 ? Number(`20${year}`) : Number(year);
        return this.createValidUtcDate(
          numericYear,
          Number(month),
          Number(day),
        )?.toISOString() ?? null;
      }
    }
    return this.parseDateValue(value);
  }

  private createValidUtcDate(
    year: number,
    month: number,
    day: number,
  ): Date | null {
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
      ? date
      : null;
  }

  private toDatabaseDate(isoDate: string): string {
    return isoDate.slice(0, 10);
  }

  private normalizeSortCode(value: any): string | undefined {
    const raw = this.asString(value);
    if (!raw) {
      return undefined;
    }
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      return undefined;
    }
    if (digits.length >= 6) {
      return digits.slice(0, 6);
    }
    return digits.padStart(6, "0");
  }

  private asString(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    const raw = typeof value === "string" ? value : String(value);
    // APS transaction extracts represent empty fields with NUL bytes. Those
    // cannot be sent to PostgreSQL text columns, so treat them as empty input.
    return this.decodePossiblyBinaryText(raw).replace(/\u0000/g, "").trim();
  }

  private decodePossiblyBinaryText(value: string): string {
    if (!value) {
      return "";
    }
    if (this.isLikelyReadable(value)) {
      return value;
    }
    const bytes = this.stringToBytes(value);
    const decoders: string[] = ["utf-8", "utf-16le", "utf-16be"];
    for (const encoding of decoders) {
      try {
        const decoded = new TextDecoder(encoding, { fatal: false }).decode(
          bytes,
        );
        if (this.isLikelyReadable(decoded)) {
          return decoded;
        }
      } catch {
        continue;
      }
    }
    return value;
  }

  private stringToBytes(value: string): Uint8Array {
    const result = new Uint8Array(value.length);
    for (let i = 0; i < value.length; i++) {
      result[i] = value.charCodeAt(i) & 0xff;
    }
    return result;
  }

  private isLikelyReadable(value: string): boolean {
    if (!value) {
      return false;
    }
    let readable = 0;
    for (let i = 0; i < value.length; i++) {
      const code = value.charCodeAt(i);
      if (
        code === 9 ||
        code === 10 ||
        code === 13 ||
        (code >= 32 && code <= 126) ||
        (code >= 0xa0 && code <= 0x024f)
      ) {
        readable++;
      }
    }
    return readable / value.length > 0.6;
  }

  private getCellValue(worksheet: XLSX.WorkSheet | null, address: string): any {
    if (!worksheet) {
      return null;
    }
    const cell = worksheet[address];
    if (!cell) {
      return null;
    }
    return cell.v ?? cell.w ?? null;
  }

  private parseDateFromWorkbookProps(workBook: XLSX.WorkBook): string | null {
    const created = workBook?.Props?.CreatedDate;
    if (created instanceof Date && !isNaN(created.getTime())) {
      return created.toISOString();
    }
    const modified = workBook?.Props?.ModifiedDate;
    if (modified instanceof Date && !isNaN(modified.getTime())) {
      return modified.toISOString();
    }
    return null;
  }

  private findDateInWorksheet(worksheet: XLSX.WorkSheet | null): string | null {
    if (!worksheet) {
      return null;
    }
    const ref = worksheet["!ref"];
    if (!ref) {
      return null;
    }
    const range = XLSX.utils.decode_range(ref);
    const searchRange = {
      s: { c: range.s.c, r: range.s.r },
      e: { c: range.e.c, r: Math.min(range.s.r + 10, range.e.r) },
    };
    const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, {
      header: 1,
      raw: false,
      range: searchRange,
      blankrows: false,
    }) as any[][];

    for (const row of rows) {
      for (const cell of row) {
        if (typeof cell !== "string") {
          continue;
        }
        const direct = this.parseDateValue(cell);
        if (direct) {
          return direct;
        }
        const extracted = this.extractDateSubstring(cell);
        if (extracted) {
          return extracted;
        }
      }
    }
    return null;
  }

  private extractDateSubstring(value: string): string | null {
    if (!value) {
      return null;
    }
    const match = value.match(/(\d{1,2})\s*([A-Za-z]{3,9})\s*(\d{2,4})/);
    if (!match) {
      return null;
    }
    const [, day, monthName, yearPart] = match;
    const month = this.monthNameToNumber(monthName);
    if (month === null) {
      return null;
    }
    const year =
      yearPart?.length === 2
        ? Number(`20${yearPart}`)
        : Number(yearPart || new Date().getFullYear());
    const date = new Date(Date.UTC(year, month, Number(day)));
    if (isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString();
  }

  private parseDateFromFileName(fileName?: string): string | null {
    if (!fileName) {
      return null;
    }
    const match = fileName.match(/(\d{1,2})\s*([A-Za-z]{3,9})\s*(\d{2,4})?/);
    if (!match) {
      return null;
    }
    const [, day, monthName, yearPart] = match;
    const month = this.monthNameToNumber(monthName);
    if (month === null) {
      return null;
    }
    const currentYear = new Date().getFullYear();
    const year =
      yearPart?.length === 2
        ? Number(`20${yearPart}`)
        : Number(yearPart || currentYear);
    const date = new Date(Date.UTC(year, month, Number(day)));
    if (isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString();
  }

  private monthNameToNumber(month?: string): number | null {
    if (!month) {
      return null;
    }
    const normalized = month.trim().toLowerCase();
    const months = [
      "jan",
      "feb",
      "mar",
      "apr",
      "may",
      "jun",
      "jul",
      "aug",
      "sep",
      "oct",
      "nov",
      "dec",
    ];
    const index = months.findIndex((name) => normalized.startsWith(name));
    return index >= 0 ? index : null;
  }

  private notifyError(summary: string, detail?: string) {
    this.messageService.add({
      severity: "error",
      summary,
      detail,
      life: 7000,
    });
  }

  private extractErrorMessage(
    error: unknown,
    fallback?: string,
  ): string | undefined {
    if (!error) {
      return fallback;
    }
    if (typeof error === "string") {
      return error;
    }
    if (error instanceof Error) {
      return error.message || fallback;
    }
    return fallback;
  }

  private isAbortError(error: unknown): boolean {
    return (
      !!error &&
      typeof error === "object" &&
      "name" in error &&
      (error as { name?: string }).name === "AbortError"
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.lifecycleAbort.abort();
    this.clearScheduledTasks();
    if (this.activeFileReader) {
      try {
        this.activeFileReader.abort();
      } catch {
        // ignore FileReader abort errors during teardown
      }
      this.activeFileReader = null;
    }
  }

  private scheduleTask(callback: () => void, delay = 0) {
    const timeout = setTimeout(() => {
      this.timeouts.delete(timeout);
      if (this.destroyed) {
        return;
      }
      callback();
    }, delay);
    this.timeouts.add(timeout);
  }

  private clearScheduledTasks() {
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts.clear();
  }
}
